import { i18n } from "@/shared/i18n"
import { useInterface } from "@/shared/ui"
import { appVersion, getCurrentLanguage } from "@/shared/utils"
import { App } from "@capacitor/app"
import { isPlatform } from "@ionic/vue"
import type { Router } from "vue-router"
import { checkAppVersionApi } from "./api"
import { EAppVersionCheckDecision, IAppVersionCheckRequest, IAppVersionCheckResponse } from "./model"
import { useAppVersionStore } from "./store"

// Универсальный обработчик ошибок
function handleError(error: any, fallback: string) {
    if (error instanceof Error) return error.message
    if (typeof error === "string") return error
    return fallback
}

export function useAppVersionCheck() {
    async function prepareData(): Promise<IAppVersionCheckRequest> {
        try {
            let appNameString = ""
            let appIdString = ""
            let appBuildString = ""
            let appVersionString = ""
            let appOsString = ""
            let appLanguageString = ""
            let appTimezoneString = ""

            if ((isPlatform("capacitor") && !isPlatform("electron"))) {
                const appFullVersion = await App.getInfo()
                appNameString = appFullVersion.name
                appIdString = appFullVersion.id
                appBuildString = appFullVersion.build
                appVersionString = appFullVersion.version
            }
            else
                appVersionString = appVersion

            if (isPlatform("ios") || isPlatform("iphone") || isPlatform("ipad"))
                appOsString = "ios"
            else if (isPlatform("android"))
                appOsString = "android"

            appLanguageString = getCurrentLanguage()
            appTimezoneString = Intl.DateTimeFormat().resolvedOptions().timeZone

            return {
                name: appNameString,
                id: appIdString,
                build: appBuildString,
                version: appVersionString,
                os: appOsString,
                language: appLanguageString,
                timezone: appTimezoneString
            }
        }
        catch (error) {
            throw handleError(error, "Failed to prepare data to app version check")
        }
    }

    async function checkAppVersion(): Promise<EAppVersionCheckDecision> {
        const appVersionStore = useAppVersionStore()

        try {
            const requestData = await prepareData()

            if (!appVersionStore.needToCheckAppVersion(requestData.version))
                return appVersionStore.checkDecision

            const result = await checkAppVersionApi(requestData) as IAppVersionCheckResponse

            appVersionStore.saveCurrentTimestampAndVersion(requestData.version)
            appVersionStore.appStoreUrl = result.downloadUrl

            if (!result.isSupported) {
                appVersionStore.checkDecision = EAppVersionCheckDecision.NotSupported
                return EAppVersionCheckDecision.NotSupported
            }
            else if (result.isSupported && !result.isLatest) {
                appVersionStore.checkDecision = EAppVersionCheckDecision.UpdateAvailable
                return EAppVersionCheckDecision.UpdateAvailable
            }
            else {
                appVersionStore.checkDecision = EAppVersionCheckDecision.UpToDate
                return EAppVersionCheckDecision.UpToDate
            }
        }
        catch (error) {
            throw handleError(error, "Failed to check app version")
        }
    }

    async function makeDecisionBasedOnAppVersionCheck(router: Router): Promise<void> {
        const appVersionStore = useAppVersionStore()

        try {
            const result = await checkAppVersion()
            if (result === EAppVersionCheckDecision.UpToDate)
                return
            else if (result === EAppVersionCheckDecision.UpdateAvailable && appVersionStore.appStoreUrl) {
                const { showNotificationCompactWithButton } = useInterface()
                await showNotificationCompactWithButton(
                    i18n.global.t("Interface.AppVersionCheck.UpdateAvailable"),
                    "➔",
                    () => window.open(appVersionStore.appStoreUrl, "_system"),
                    "primary",
                    10000)
            }
            else if (result === EAppVersionCheckDecision.NotSupported) {
                if (appVersionStore.appStoreUrl)
                    router.replace({ name: "Deprecated", query: { url: JSON.stringify(appVersionStore.appStoreUrl) } })
                else
                    router.replace({ name: "Deprecated" })
            }
        }
        catch (error) {
            throw handleError(error, "Failed to make decision based on app version check")
        }
    }

    return { makeDecisionBasedOnAppVersionCheck }
}
